import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectCard from "@/components/ProjectCard";
import { Heart, MessageCircle } from "lucide-react";
import { formatPrice } from "@/lib/api";

const STAGE_LABELS = {
  new: "New", contacted: "Contacted", site_visit: "Site Visit", negotiation: "Negotiation",
  booked: "Booked", registered: "Registered", dropped: "Dropped",
};

const STAGE_COLORS = {
  new: "bg-blue-50 text-blue-700",
  contacted: "bg-amber-50 text-amber-700",
  site_visit: "bg-purple-50 text-purple-700",
  negotiation: "bg-orange-50 text-orange-700",
  booked: "bg-emerald-50 text-emerald-700",
  registered: "bg-forest/10 text-forest",
  dropped: "bg-red-50 text-red-700",
};

export default function BuyerProfile() {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("favorites");

  useEffect(() => {
    api.get("/me/favorites").then((r) => setFavs(r.data)).catch(() => setFavs([]));
    api.get("/me/inquiries").then((r) => setInquiries(r.data)).catch(() => setInquiries([]));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-2">Your account</div>
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
        <p className="text-muted-foreground text-sm mt-2">Your saved projects and inquiry history in one place.</p>
      </div>

      <div className="flex gap-1 border-b border-border mb-8">
        {["favorites", "inquiries"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t ? "border-gold text-forest" : "border-transparent text-muted-foreground hover:text-forest"
            }`}
          >
            {t === "favorites" ? `Favorites (${favs.length})` : `Inquiries (${inquiries.length})`}
          </button>
        ))}
      </div>

      {tab === "favorites" && (
        favs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-14 text-center">
            <Heart className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-heading font-bold text-forest">No favorites yet</div>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Tap the heart on any project card to save it.</p>
            <Link to="/projects" className="inline-flex rounded-full bg-forest text-white hover:bg-forest-hover px-5 py-2.5 text-sm font-medium">Browse projects</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
            {favs.map((p) => <ProjectCard key={p.id} project={p} />)}
          </div>
        )
      )}

      {tab === "inquiries" && (
        inquiries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-14 text-center">
            <MessageCircle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-heading font-bold text-forest">No inquiries yet</div>
            <p className="text-sm text-muted-foreground mt-1 mb-4">When you request a callback or site visit, it'll show up here.</p>
            <Link to="/projects" className="inline-flex rounded-full bg-forest text-white hover:bg-forest-hover px-5 py-2.5 text-sm font-medium">Explore projects</Link>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="inquiries-list">
            <table className="w-full text-sm">
              <thead className="bg-cream text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Project</th>
                  <th className="text-left px-4 py-3 font-medium">Message</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((l) => (
                  <tr key={l.id} className="border-t border-border/60">
                    <td className="px-4 py-3">
                      <div className="text-forest font-medium">{l.project_name || "General inquiry"}</div>
                      {l.budget && <div className="text-xs text-muted-foreground mt-0.5">Budget · {formatPrice(l.budget)}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground max-w-[280px]"><span className="italic line-clamp-2">{l.message || "—"}</span></td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_COLORS[l.stage] || "bg-cream text-muted-foreground"}`}>{STAGE_LABELS[l.stage] || l.stage}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
